import { createContext, useContext, useMemo, useState } from 'react';

import { atom, createStore, useAtom } from 'jotai';

import { DonationStoreItem } from '../../api/new/types/donations';

type IDisplaySection = 1 | 2;

type IPreviewData = {
  isPreview?: 'desktop' | 'mobile';
  previewData?: Partial<DonationStoreItem>;
};

type IOnLastStepComplete = () => void;

type TempDonationsContext = {
  value: {
    onLastStepComplete?: IOnLastStepComplete;
    isPreview?: boolean;
  };
  store: ReturnType<typeof createStore>;
};

// Specific to Simple donations Flow
const willSupportCauseAtom = atom<boolean | undefined>(undefined);
willSupportCauseAtom.debugLabel = 'simpleDonations_willSupportCause';

const displaySectionAtom = atom<IDisplaySection>(1);
displaySectionAtom.debugLabel = 'simpleDonations_displaySection';

const simpleDonationPreviewAtom = atom<IPreviewData | undefined>(undefined);
simpleDonationPreviewAtom.debugLabel = 'simpleDonations_previewAtom';

const SimpleDonationContext = createContext<TempDonationsContext | undefined>(undefined);

const SimpleDonationFlowProvider = ({
  isPreview = false,
  children,
  onLastStepComplete,
}: {
  children: React.ReactNode;
  onLastStepComplete?: IOnLastStepComplete;
  isPreview?: boolean;
}) => {
  const [store] = useState(() => createStore());
  const value = useMemo(
    () => ({
      isPreview: isPreview,
      onLastStepComplete,
    }),
    [isPreview, onLastStepComplete]
  );

  return (
    <SimpleDonationContext.Provider value={{ value: value, store }}>
      {children}
    </SimpleDonationContext.Provider>
  );
};

function useSimpleDonations() {
  const context = useContext(SimpleDonationContext);
  if (context === undefined) {
    throw new Error('useSimpleDonations must be used within a useSimpleDonations!');
  }

  const { store, value } = context;

  // Returning the value here is fine because this would only apply during a preview.
  const [displaySection, setDisplaySection] = useAtom(displaySectionAtom, { store });
  const [willSupportCause, setWillSupportCause] = useAtom(willSupportCauseAtom, { store });

  return {
    displaySection,
    setDisplaySection,
    onLastStepComplete: value.onLastStepComplete,

    willSupportCause,
    setWillSupportCause,

    isPreview: value.isPreview,
  };
}

export { SimpleDonationFlowProvider, useSimpleDonations };
